import { Component, OnInit, Output, EventEmitter, Inject, LOCALE_ID, Input, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PayInfoFilters } from './pay-info-filters.model';
import { PayGroup } from '../../../shared/models/pay-group.model';
import { PayGroupService } from '../../../services/paygroup.service';
import { PayrollCycleType, PayrollTypeInfo } from '../../../services/payroll-types.service';
import { PayrollTypeService } from '../../../services/payroll-types.service';
import { map, takeUntil, concatMap, toArray } from 'rxjs/operators';
import { PayInfoFiltersService } from './pay-info-filters.service';
import { sortBy } from 'lodash';
import { BaseObserver } from '../../../shared/baseobserver';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { PayInfoView } from '../pay-info-view.model';
import { Subscription } from 'rxjs';
import { UltiCurrencyPipe } from '../../../shared/pipes/ulticurrency.pipe';
import { PayCustomerItemStatus } from '../../../shared/enums/smarthub-enums';
import { FeatureToggleService } from '../../../shared/services/feature-toggle/feature-toggle.service';
import { Observable, from } from 'rxjs';
import { EntitlementsService } from '../../../entitlements/entitlements.service';
import * as moment from 'moment';

class LabeledStatus {
  constructor(public value: PayCustomerItemStatus, public label: string) { }
}

export class SearchablePayInfoView {
  readonly view: PayInfoView;
  readonly key: string;
  localizedName: string;

  constructor(view: PayInfoView) {
    this.view = view;
    this.key = SearchablePayInfoView.getPayInfoViewTextKey(this.view);
  }

  static getPayInfoViewTextKey(view: PayInfoView): string {
    switch (view) {
      case PayInfoView.StopPayRequest:
        return 'EMPLOYEE_PAYS.VIEW_STOP_PAY';
      case PayInfoView.CancelStopPayRequest:
        return 'EMPLOYEE_PAYS.VIEW_CANCEL_STOP_PAY';
      case PayInfoView.EmployeePays:
      default:
        return 'EMPLOYEE_PAYS.VIEW_EMPLOYEE_PAYS';
    }
  }

  withLocalizedName(localizedName: string) {
    this.localizedName = localizedName;
    return this;
  }
}

@Component({
  selector: 'ssg-pay-info-filters',
  templateUrl: './pay-info-filters.component.html',
  styleUrls: ['./pay-info-filters.component.less']
})
export class PayInfoFiltersComponent extends BaseObserver implements OnInit, OnDestroy {
  @Output() toggle: EventEmitter<boolean>;
  readonly stoppableStatusKeys = [
    PayCustomerItemStatus.CollectionPartiallyCollected.valueOf(),
    PayCustomerItemStatus.CollectionHold.valueOf(),
    PayCustomerItemStatus.CollectionInTransit.valueOf(),
    PayCustomerItemStatus.CollectionCollected.valueOf(),
    PayCustomerItemStatus.PaymentReady.valueOf(),
    PayCustomerItemStatus.PaymentCheckPaid.valueOf(),
    PayCustomerItemStatus.PaymentHold.valueOf(),
    PayCustomerItemStatus.CollectionReady.valueOf()
  ];
  readonly unstoppableStatusKeys = [
    PayCustomerItemStatus.PayCanceledBeforePaid.valueOf(),
    PayCustomerItemStatus.PayCanceledAfterPaid.valueOf(),
    PayCustomerItemStatus.PayStopRequested.valueOf(),
    PayCustomerItemStatus.PayStopInProgress.valueOf(),
    PayCustomerItemStatus.PayDeleted.valueOf(),
    PayCustomerItemStatus.PayRejected.valueOf(),
    PayCustomerItemStatus.PayReturned.valueOf(),
    PayCustomerItemStatus.PayReversed.valueOf()
  ];

  private _amountStripRegExp: RegExp;
  private _amountDecimalRegExp: RegExp;
  private _amountMinusRegExp: RegExp;

  public formattedAmount: string;
  allViews: SearchablePayInfoView[];

  minDate = moment({ year: 2018, month: 8, day: 1 });

  private _lastFilters: PayInfoFilters;
  private _subscriptions: Subscription[];
  editFilters: PayInfoFilters;
  allPayGroups: PayGroup[] = [];
  allPayrollTypes: PayrollTypeInfo[] = [];
  allProducts = ['AT_A_GLANCE.PRODUCT_EMPLOYEE_PAY'];
  allStatuses: LabeledStatus[];
  stoppableStatuses: LabeledStatus[];
  unstoppableStatuses: LabeledStatus[];
  availableStatuses: LabeledStatus[];

  isEditing = false;
  isInvalid = false;

  @Input() isWorking: boolean;

  ukgSelectCompareWith(currentValue, compareValue): boolean {
    return currentValue.toString() === compareValue.toString();
  }

  get view(): PayInfoView {
    return this.editFilters && this.editFilters.view;
  }

  get showToDate(): boolean {
    return this.view === PayInfoView.EmployeePays;
  }

  get isStopPaymentRequestEnabled(): boolean {
    return this._productConfigService.isFeatureEnabled('enableStopPaymentRequest');
  }

  get selectedView(): SearchablePayInfoView {
    return this.editFilters && this.allViews.find(view => view.view === this.view);
  }
  set selectedView(value: SearchablePayInfoView) {
    this.editFilters.statuses = [];
    this.editFilters.view = value.view;
    switch (value.view) {
      case  PayInfoView.StopPayRequest:
        this.availableStatuses = this.stoppableStatuses;
        break;
      case PayInfoView.CancelStopPayRequest:
        this.availableStatuses = this.unstoppableStatuses;
        break;
      default:
        this.availableStatuses = this.allStatuses;
    }
  }

  constructor(private readonly _payGroupService: PayGroupService,
    private readonly _payrollTypeService: PayrollTypeService,
    private readonly _filtersService: PayInfoFiltersService,
    private readonly _ultiCurrencyPipe: UltiCurrencyPipe,
    private readonly _productConfigService: FeatureToggleService,
    private readonly _translateService: TranslateService,
    private readonly _entitlementsService: EntitlementsService,
    private readonly _featureToggleService: FeatureToggleService,
    translateService: TranslateService,
    @Inject(LOCALE_ID) locale: string) {

    super();
    const groupSymbol = getLocaleNumberSymbol(locale, NumberSymbol.CurrencyGroup);
    const plusSymbol = getLocaleNumberSymbol(locale, NumberSymbol.PlusSign);
    const decimalSymbol = getLocaleNumberSymbol(locale, NumberSymbol.CurrencyDecimal);
    const minusSymbol = getLocaleNumberSymbol(locale, NumberSymbol.MinusSign);
    this._amountStripRegExp = new RegExp('[$' + '\\' + groupSymbol + '\\' + plusSymbol + ']', 'g');
    this._amountDecimalRegExp = new RegExp('\\' + decimalSymbol, 'g');
    this._amountMinusRegExp = new RegExp('\\' + minusSymbol, 'g');
    this.toggle = new EventEmitter<boolean>();
    this.allStatuses = this._generateStatuses(Object.keys(PayCustomerItemStatus).map(Number), translateService, locale);
    this.stoppableStatuses = this._generateStatuses(this.stoppableStatusKeys, translateService, locale);
    this.unstoppableStatuses = this._generateStatuses(this.unstoppableStatusKeys, translateService, locale);
    this.availableStatuses = this.allStatuses;
  }

  ngOnInit() {
    this.getAllPayGroups();
    this.initPayrollTypes();
    this.initQuickFilters();

    this._lastFilters = null;
    this._subscriptions = [];

    this._subscriptions.push(this._filtersService.getFilters().subscribe(filters => this._handleFiltersUpdated(filters)));
  }

  ngOnDestroy() {
    if (this._subscriptions) {
      for (const subscription of this._subscriptions) {
        subscription.unsubscribe();
      }

      this._subscriptions = null;
    }
  }

  initQuickFilters() {
    const rawViews = [PayInfoView.EmployeePays];

    if (this._entitlementsService.isEntitled(['CuiStopPaymentRequest'])
      && this.isStopPaymentRequestEnabled)  {

      rawViews.push(PayInfoView.StopPayRequest);
      rawViews.push(PayInfoView.CancelStopPayRequest);
    }
    this._createPayInfoViews(rawViews).subscribe(views => {
      this.allViews = views;
    });
  }

  private _createPayInfoViews(views: PayInfoView[]): Observable<SearchablePayInfoView[]> {
    const searchableViews = views.map(view => new SearchablePayInfoView(view));

    return from(searchableViews)
      .pipe(
        concatMap(view => this._translateService.get(view.key)),
        map(
          (localizedName, i) => searchableViews[i].withLocalizedName(localizedName)
        ),
        toArray()
      );
  }

  applyEdits() {
    if (this.editFilters.isFormInvalid() || this.isWorking) {
      return;
    }

    this._filtersService.updateFilters(this.editFilters);
  }

  clearEditFilters() {
    this.editFilters.reset();
    this.formattedAmount = this.formatAmount(this.editFilters.amount);
    this.applyEdits();
  }

  toggleFilters(value: boolean) {
    this.toggle.emit(value);
  }

  getCode(payGroup: PayGroup) {
    return payGroup.code.trim();
  }

  initPayrollTypes() {
    this._payrollTypeService.getPayrollTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => this.allPayrollTypes = result);
  }

  getAllPayGroups(): void {
    this._payGroupService.getPayGroups()
      .pipe(
        map(groups => sortBy(groups, ['code', 'name'])),
        takeUntil(this.destroy$)
      )
      .subscribe(result => this.allPayGroups = result);
  }

  private formatAmount(amount: number) {
    const formattedValue = this._ultiCurrencyPipe.transform(amount, '1.02', undefined, true);
    return formattedValue === null ? '' : formattedValue;
  }

  amountChanged(value: string) {
    const numText = value
      .replace(this._amountStripRegExp, '')
      .replace(this._amountDecimalRegExp, '.')
      .replace(this._amountMinusRegExp, '-')
      .replace(/\s/g, '');
    const num = Number(numText);
    if (numText === '' || isNaN(num) || num === null) {
      this.editFilters.amount = null;
    } else {
      this.editFilters.amount = Number(num.toFixed(2));
    }
    const formatted = this.formatAmount(this.editFilters.amount);
    if (this.formattedAmount === formatted) {
      this.formattedAmount = formatted + ' ';
    } else {
      this.formattedAmount = formatted;
    }
  }

  private _handleFiltersUpdated(filters: PayInfoFilters) {
    if (this._lastFilters !== null &&
      PayInfoFilters.compareFilters(this._lastFilters, filters)) {

      return;
    }

    this.editFilters = filters.clone();
    this._lastFilters = filters;
    this.formattedAmount = this.formatAmount(this.editFilters.amount);
  }

  private _generateStatuses(keys: number[], translateService: TranslateService, locale: string): LabeledStatus[] {
    const statusDict = [];
    keys.forEach(key => {
      if (!isNaN(Number(key))) {
        const status = Number(key) as PayCustomerItemStatus;
        const label = translateService.instant(PayInfoFilters.getStatusTextKey(status));
        if (statusDict[label]) {
          statusDict[label].value += ',' + status;
        } else {
          statusDict[label] = new LabeledStatus(status, label);
        }
      }
    });
    const statuses: LabeledStatus[] = Object.values(statusDict);
    statuses.sort((a, b) => a.label.localeCompare(b.label, locale));

    return statuses;
  }

  get payrollType(): PayrollCycleType {
    return this.editFilters.dashboardFilters.payrollType;
  }

  set payrollType(data: PayrollCycleType) {
      this.editFilters.dashboardFilters.payrollType = parseInt(data.toString(), 10);
  }
}
